import React, { useEffect, useMemo } from 'react';
import { VendorOrderPage } from './VendorOrderPage';
import { useSelector } from 'react-redux';
import { getVendorOrderData, sendToSpruceTitleSearch } from 'store/vendorStore/vendorOrderData/dispatch';
import { getVendorDocuments } from 'store/vendorStore/vendorDocuments/dispatch';
import { getVendorCommentsData } from 'store/vendorStore/vendorComments/dispatch';
import {
  getTaxCertificationRequestsAsyncAction,
  sendToSpruceAsyncAction,
  sendToSpruceSlice,
} from 'store/vendorStore/vendorTaxCertificationData/slices';
import { eVendorServiceType } from '../../../types/order/title/eVendorServiceType';
import { getTaxCertificationAsyncAction } from '../../../store/order/taxCertifications/taxCertificationSlice';
import { createDefaultVendorOrderInformationData } from '../../../types/vendorDashboard/IVendorOrderInformation';
import { UUID } from '../../../types/shared/UUID';
import { ClientUrlFactory } from '../../../ui-toolkit/rest/ClientUrlFactory';
import { vendorOrderReducer } from '../../../store/vendorStore/vendorOrderData/reducer';
import { useParams } from 'react-router-dom';
import { VendorWrapper } from '../../../components/vendorDashboard/VendorWrapper';
import { VendorSelectors } from '../../../selectors/vendorPortal/VendorSelectors';
import { VendorRedirectGuard } from '../../../components/vendorDashboard/VendorRedirectGuard';
import { findError } from '../../../utilities/vendorDashboard/findError';
import { isLoading } from '../../../utilities/vendorDashboard/isLoading';
import { findRequiredAndOptionalVendorDocuments } from 'utilities/vendorDashboard/findRequiredAndOptionalVendorDocuments';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../API/documentCategories/DocumentCategoriesApi';
import { useAppDispatch } from '../../../store/store';

export interface IVendorOrderContainerProps {}

export const VendorOrderContainer: React.FC<IVendorOrderContainerProps> = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const dispatch = useAppDispatch();
  const { order, orderError, orderFetchStatus, sentToSpruceError, sentToSpruceFetchStatus } = useSelector(
    VendorSelectors.selectOrder
  );
  const [updateInformation, setUpdateInformation] = React.useState(false);
  const { documents, documentsError } = useSelector(VendorSelectors.selectDocuments);
  const { getCommentsFetchStatus, getCommentsError, postCommentError, postCommentFetchStatus } = useSelector(
    VendorSelectors.selectComments
  );
  const {
    sendToSpruceSlice: { fetchStatus: sendTaxCertToSpruceFetchStatus, fetchError: sendTaxCertToSpruceError },
    taxCertificationSlice: taxCertificationList,
  } = useSelector(VendorSelectors.selectTaxCertification);
  const { data: { activeCategories } = defaultNormalizedDocumentCategoriesData } = useListDocumentCategoriesQuery();

  const vendorOrder = order[orderId] || createDefaultVendorOrderInformationData();

  const pageError = useMemo(() => {
    return findError(
      orderError,
      documentsError,
      getCommentsError,
      postCommentError,
      sentToSpruceError,
      sendTaxCertToSpruceError?.message
    );
  }, [orderError, documentsError, getCommentsError, postCommentError, sentToSpruceError, sendTaxCertToSpruceError]);

  const pageLoading = useMemo(() => {
    return isLoading(
      orderFetchStatus.fetching,
      getCommentsFetchStatus.fetching,
      sentToSpruceFetchStatus.fetching,
      sendTaxCertToSpruceFetchStatus.fetching
    );
  }, [orderFetchStatus, getCommentsFetchStatus, sentToSpruceFetchStatus, sendTaxCertToSpruceFetchStatus]);

  useEffect(() => {
    if (!updateInformation) {
      // update info if user return from another page
      dispatch(vendorOrderReducer.actions.reset());
      dispatch(sendToSpruceSlice.actions.reset({}));
      dispatch(getVendorOrderData(orderId));
      dispatch(getVendorDocuments(orderId));
      dispatch(getVendorCommentsData(orderId, [eVendorServiceType.titleSearchRequest]));
      setUpdateInformation(true);
    }
  }, [dispatch, orderId, updateInformation]);

  const onSendToSpruce = async () => {
    try {
      dispatch(sendToSpruceTitleSearch(orderId, order[orderId].titleSearchId));
    } catch (e: any) {}
  };

  const { requiredDocuments, optionalDocuments } = useMemo(() => {
    const orderDocuments = documents[orderId] ?? [];
    return findRequiredAndOptionalVendorDocuments(activeCategories, orderDocuments);
  }, [orderId, documents, activeCategories]);

  useEffect(() => {
    if (vendorOrder.taxCertificationRequestId) {
      dispatch(getTaxCertificationRequestsAsyncAction(orderId));
    }
    dispatch(getTaxCertificationAsyncAction(orderId));
  }, [dispatch, orderId, vendorOrder.taxCertificationRequestId]);

  const existingTaxCertRequestId = vendorOrder.taxCertificationRequestId;
  const onSendTaxCertToSpruce = async () => {
    if (existingTaxCertRequestId) {
      dispatch(sendToSpruceAsyncAction(existingTaxCertRequestId));
    }
  };

  const existingTaxCert =
    taxCertificationList.data.taxCertifications.length > 0 ? taxCertificationList.data.taxCertifications[0] : undefined;

  return (
    <VendorRedirectGuard
      redirect={(sentToSpruceFetchStatus.success || sendTaxCertToSpruceFetchStatus.success) && updateInformation}
      to={ClientUrlFactory().urlFor.vendorOrders()}
      push
    >
      <VendorWrapper isLoading={pageLoading}>
        <VendorOrderPage
          orderId={orderId}
          order={vendorOrder}
          pageError={pageError}
          documents={documents[orderId] || []}
          requiredDocuments={requiredDocuments}
          optionalDocuments={optionalDocuments}
          uploadNewCommentFetch={postCommentFetchStatus}
          onSendToSpruce={onSendToSpruce}
          sendToSpruceFetchStatus={sentToSpruceFetchStatus}
          sendToSpruceError={sentToSpruceError}
          taxCert={existingTaxCert}
          onSendTaxCertToSpruce={onSendTaxCertToSpruce}
        />
      </VendorWrapper>
    </VendorRedirectGuard>
  );
};
